import type { SideNavV2Item, SideNavV2Section } from "@/components/interactive/SideNavV2";
import {
  findProjectNavCategoryListingRow,
  getProjectNavCategorySidebarSections,
} from "@/components/navigation/project-category-nav";
import {
  EMPTY_RAIL_CATEGORY_ORDER,
  applyStoredOrder,
  mergeVisibleOrder,
} from "@/components/navigation/project-nav-rail-pins";
import {
  migrateV1SectionOrder,
  migrateV1SidebarRowLayout,
} from "@/components/navigation/project-nav-sidebar-layout-migration";
import {
  useCanArrangeCategoryNav,
  useShowsArrangedCategoryNav,
} from "@/utils/hooks/use-category-nav-enabled";
import { useLocalStorageSyncedState } from "@/utils/hooks/use-local-storage-synced-state";
import { LocalStorageKey } from "@/utils/local-storage-utils";
import { useCallback, useMemo } from "react";

/**
 * The default sidebars stored arrangements are made against. Raise it with a migration when a
 * change to them would leave stored ids naming sections that are gone.
 */
export const SIDEBAR_LAYOUT_VERSION = 2;

/** A store's layout version; one stored before versioning is version 1. */
const storedLayoutVersion = (store: Record<string, unknown> | null | undefined) =>
  typeof store?.version === "number" ? store.version : 1;

/** The reader's arrangement of the category sidebars' rows, kept per browser. */
export interface SidebarRowLayout extends Record<string, unknown> {
  /** The `SIDEBAR_LAYOUT_VERSION` the layout was arranged against. */
  version: number;
  /** Each section's row order, by section id. */
  orderBySection: Record<string, string[]>;
  /** Rows the reader moved out of their own section: the section each now sits in. */
  sectionByRow: Record<string, string>;
  /** Rows the reader moved to another category's sidebar: the category each now sits in. */
  categoryByRow: Record<string, string>;
  /** Titles the reader gave sections, by section id. */
  labelBySection: Record<string, string>;
  /** Sections the reader deleted once they had no rows. */
  deletedSectionIds: string[];
  /** Sections the reader added to each category's sidebar, by category id. */
  addedSectionIdsByCategory: Record<string, string[]>;
  /** Names the reader gave the rail's categories, by category id. */
  labelByCategory: Record<string, string>;
}

export const EMPTY_SIDEBAR_ROW_LAYOUT: SidebarRowLayout = {
  version: SIDEBAR_LAYOUT_VERSION,
  orderBySection: {},
  sectionByRow: {},
  categoryByRow: {},
  labelBySection: {},
  deletedSectionIds: [],
  addedSectionIdsByCategory: {},
  labelByCategory: {},
};

/** The title a section the reader adds starts with. */
export const NEW_SIDEBAR_SECTION_LABEL = "New section";

const storedIds = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((id): id is string => typeof id === "string") : [];

/**
 * The rest of a layout, carried over as one part of it changes. It keeps the version the layout
 * was arranged against, so a layout changed before it is migrated is still migrated on read.
 */
const layoutFields = (layout: Partial<SidebarRowLayout> | null | undefined): SidebarRowLayout => ({
  version: storedLayoutVersion(layout),
  orderBySection: { ...layout?.orderBySection },
  sectionByRow: { ...layout?.sectionByRow },
  categoryByRow: { ...layout?.categoryByRow },
  labelBySection: { ...layout?.labelBySection },
  deletedSectionIds: storedIds(layout?.deletedSectionIds),
  addedSectionIdsByCategory: { ...layout?.addedSectionIdsByCategory },
  labelByCategory: { ...layout?.labelByCategory },
});

/**
 * A stored layout as the current default sidebars read it, migrated from the version it was
 * arranged against, so the reader's arrangement survives changes to the defaults.
 */
export const migrateSidebarRowLayout = (
  layout: Partial<SidebarRowLayout> | null | undefined,
): SidebarRowLayout => {
  const fields = layoutFields(layout);
  return fields.version < 2
    ? { ...migrateV1SidebarRowLayout(fields), version: SIDEBAR_LAYOUT_VERSION }
    : fields;
};

/** The sections the reader added to a category's sidebar, empty until rows move in. */
export const getAddedSidebarSections = <T extends string>(
  layout: Partial<SidebarRowLayout> | null | undefined,
  categoryId: string,
): SideNavV2Section<T>[] =>
  storedIds(layout?.addedSectionIdsByCategory?.[categoryId]).map((id) => ({
    id,
    label: NEW_SIDEBAR_SECTION_LABEL,
    items: [],
  }));

/** A section's stored row order; stored layouts are read defensively, as they outlive releases. */
const storedRowOrder = (
  layout: Partial<SidebarRowLayout> | null | undefined,
  sectionId: string,
) => {
  const order = layout?.orderBySection?.[sectionId];
  return Array.isArray(order) ? order : [];
};

/**
 * Whether a category's rows can move to other categories and take theirs. Settings pages find
 * their row among Settings' own (`getProjectNavRouteCategories`), so Settings keeps its rows to
 * itself.
 */
export const categoryTradesRows = (category: SideNavV2Section<string>) =>
  !category.items.some((item) => item.id === "settings");

/** The untitled section that leads a category's sidebar with the rows moved into it. */
export const getMovedRowsSectionId = (categoryId: string) => `${categoryId}:moved`;

/** The category a row sits in: the one the reader moved it to while both trade rows, else its own. */
export const resolveRowCategory = <T extends string>(
  categories: readonly SideNavV2Section<T>[],
  layout: Partial<SidebarRowLayout> | null | undefined,
  rowId: T,
): SideNavV2Section<T> | undefined => {
  const home = findProjectNavCategoryListingRow(categories, rowId);
  const movedTo = layout?.categoryByRow?.[rowId];
  const destination =
    typeof movedTo === "string" && movedTo !== home?.id
      ? categories.find((category) => category.id === movedTo)
      : undefined;
  return home != null &&
    destination != null &&
    categoryTradesRows(home) &&
    categoryTradesRows(destination)
    ? destination
    : home;
};

/**
 * `category`'s default sidebar sections with the reader's moves between categories applied: rows
 * moved out drop out, a section they leave empty with them, and rows moved in lead in an untitled
 * section (`getMovedRowsSectionId`), where `applySidebarRowLayout` orders them.
 */
export const getCategorySidebarSectionsWithMoves = <T extends string>(
  category: SideNavV2Section<T>,
  categories: readonly SideNavV2Section<T>[],
  layout: Partial<SidebarRowLayout> | null | undefined,
): SideNavV2Section<T>[] => {
  const sections = getProjectNavCategorySidebarSections(category);
  const moves = Object.entries(layout?.categoryByRow ?? {});
  if (moves.length === 0 || !categoryTradesRows(category)) return sections;

  const movedRowIds = new Set(moves.map(([rowId]) => rowId));
  const sitsElsewhere = (rowId: T) =>
    movedRowIds.has(rowId) && resolveRowCategory(categories, layout, rowId)?.id !== category.id;
  // A titled section the moves empty stays, so rows can come back or the reader can delete it.
  const kept = sections.flatMap((section) => {
    const items = section.items.filter((item) => !sitsElsewhere(item.id));
    if (items.length === section.items.length) return [section];
    return items.length === 0 && section.label == null ? [] : [{ ...section, items }];
  });
  const arrivals = moves.flatMap(([rowId, categoryId]) => {
    if (categoryId !== category.id) return [];
    const home = findProjectNavCategoryListingRow(categories, rowId as T);
    if (home == null || home.id === category.id) return [];
    if (resolveRowCategory(categories, layout, rowId as T)?.id !== category.id) return [];
    const row = getProjectNavCategorySidebarSections(home)
      .flatMap((section) => section.items)
      .find((item) => item.id === rowId);
    return row == null ? [] : [row];
  });
  return arrivals.length === 0
    ? kept
    : [{ id: getMovedRowsSectionId(category.id), items: arrivals }, ...kept];
};

/**
 * `sections` with the reader's row moves, orders, and titles applied. A moved row sits in its new
 * section while both sections are on screen, and rows a section's order does not name keep their
 * default places, after the ones it does. A titled section the moves leave without rows stays,
 * unless the reader deleted it; an untitled one drops out. A deleted section that has rows, such as
 * one a flag has since filled, shows anyway. Sections with `fixedRows` neither give nor take rows.
 */
export const applySidebarRowLayout = <T extends string>(
  sections: readonly SideNavV2Section<T>[],
  layout: Partial<SidebarRowLayout> | null | undefined,
): SideNavV2Section<T>[] => {
  const sectionIds = new Set(sections.map((section) => section.id));
  const fixedSectionIds = new Set(
    sections.filter((section) => section.fixedRows === true).map((section) => section.id),
  );
  const rowsById = new Map(
    sections.flatMap((section) =>
      section.items.map((item) => [item.id as string, { item, sectionId: section.id }] as const),
    ),
  );
  const destinations = new Map<string, string>();
  for (const [rowId, sectionId] of Object.entries(layout?.sectionByRow ?? {})) {
    const row = rowsById.get(rowId);
    if (
      typeof sectionId !== "string" ||
      row == null ||
      row.sectionId === sectionId ||
      !sectionIds.has(sectionId) ||
      fixedSectionIds.has(row.sectionId) ||
      fixedSectionIds.has(sectionId)
    ) {
      continue;
    }
    destinations.set(rowId, sectionId);
  }

  const deletedSectionIds = new Set(storedIds(layout?.deletedSectionIds));
  return sections.flatMap((section) => {
    const order = storedRowOrder(layout, section.id);
    const arrivals = [...destinations]
      .filter(([, sectionId]) => sectionId === section.id)
      .flatMap(([rowId]) => {
        const row = rowsById.get(rowId);
        return row == null ? [] : [row.item];
      });
    const leaves = section.items.some((item) => destinations.has(item.id));
    const storedLabel = layout?.labelBySection?.[section.id];
    const label =
      section.label != null && typeof storedLabel === "string" ? storedLabel : section.label;
    const items =
      order.length === 0 && arrivals.length === 0 && !leaves
        ? section.items
        : applyStoredOrder(
            [...section.items.filter((item) => !destinations.has(item.id)), ...arrivals],
            order,
          );
    if (items.length === 0 && (section.label == null || deletedSectionIds.has(section.id))) {
      return [];
    }
    return items === section.items && label === section.label
      ? [section]
      : [{ ...section, items, label }];
  });
};

/**
 * The layout once the reader has rearranged rows. `arrangement` lists the rows each section shows,
 * in order; `homeSectionOf` names the section a row sits in by default. A row back in its own
 * section is no longer a move; each section's order keeps the places of rows out of view, as
 * `mergeVisibleOrder` does.
 */
export const recordSidebarRowArrangement = (
  layout: Partial<SidebarRowLayout> | null | undefined,
  arrangement: readonly { sectionId: string; itemIds: readonly string[] }[],
  homeSectionOf: (rowId: string) => string | undefined,
): SidebarRowLayout => {
  const orderBySection: Record<string, string[]> = { ...layout?.orderBySection };
  const sectionByRow: Record<string, string> = { ...layout?.sectionByRow };
  for (const { sectionId, itemIds } of arrangement) {
    orderBySection[sectionId] = mergeVisibleOrder(storedRowOrder(layout, sectionId), itemIds);
    for (const rowId of itemIds) {
      if (homeSectionOf(rowId) === sectionId) delete sectionByRow[rowId];
      else sectionByRow[rowId] = sectionId;
    }
  }
  return { ...layoutFields(layout), orderBySection, sectionByRow };
};

/**
 * The layout once the reader has moved a row to another category's sidebar: it leads that
 * sidebar's moved rows, out of any section it was moved to before. Moved back to `homeCategoryId`,
 * it returns to its default place there.
 */
export const moveSidebarRowToCategory = (
  layout: Partial<SidebarRowLayout> | null | undefined,
  rowId: string,
  categoryId: string,
  homeCategoryId: string,
): SidebarRowLayout => {
  const categoryByRow: Record<string, string> = { ...layout?.categoryByRow };
  const sectionByRow: Record<string, string> = { ...layout?.sectionByRow };
  const orderBySection: Record<string, string[]> = { ...layout?.orderBySection };
  delete sectionByRow[rowId];
  if (categoryId === homeCategoryId) {
    delete categoryByRow[rowId];
  } else {
    categoryByRow[rowId] = categoryId;
    const movedSectionId = getMovedRowsSectionId(categoryId);
    orderBySection[movedSectionId] = [
      rowId,
      ...storedRowOrder(layout, movedSectionId).filter((id) => id !== rowId),
    ];
  }
  return { ...layoutFields(layout), categoryByRow, orderBySection, sectionByRow };
};

/**
 * The layout once the reader has renamed a section. A blank title, or the section's own
 * (`defaultLabel`), clears the rename.
 */
export const renameSidebarSection = (
  layout: Partial<SidebarRowLayout> | null | undefined,
  sectionId: string,
  label: string,
  defaultLabel: string | undefined,
): SidebarRowLayout => {
  const next = layoutFields(layout);
  const trimmed = label.trim();
  if (trimmed === "" || trimmed === defaultLabel) delete next.labelBySection[sectionId];
  else next.labelBySection[sectionId] = trimmed;
  return next;
};

/** `categories` under the names the reader gave them. */
export const applySidebarCategoryLabels = <T extends string>(
  categories: readonly SideNavV2Section<T>[],
  layout: Partial<SidebarRowLayout> | null | undefined,
): SideNavV2Section<T>[] =>
  categories.map((category) => {
    const label = layout?.labelByCategory?.[category.id];
    return typeof label === "string" && category.label != null ? { ...category, label } : category;
  });

/**
 * The layout once the reader has renamed a category, which its rail label and sidebar title then
 * show. A blank name, or the category's own (`defaultLabel`), clears the rename.
 */
export const renameSidebarCategory = (
  layout: Partial<SidebarRowLayout> | null | undefined,
  categoryId: string,
  label: string,
  defaultLabel: string | undefined,
): SidebarRowLayout => {
  const next = layoutFields(layout);
  const trimmed = label.trim();
  if (trimmed === "" || trimmed === defaultLabel) delete next.labelByCategory[categoryId];
  else next.labelByCategory[categoryId] = trimmed;
  return next;
};

/** The layout once the reader has added a section to a category's sidebar. */
export const addSidebarSection = (
  layout: Partial<SidebarRowLayout> | null | undefined,
  categoryId: string,
  sectionId: string,
): SidebarRowLayout => {
  const next = layoutFields(layout);
  next.addedSectionIdsByCategory[categoryId] = [
    ...storedIds(next.addedSectionIdsByCategory[categoryId]).filter((id) => id !== sectionId),
    sectionId,
  ];
  return next;
};

/**
 * The layout once the reader has deleted an empty section: one they added is gone for good, and
 * one of the sidebar's own stays out of view while it has no rows.
 */
export const deleteSidebarSection = (
  layout: Partial<SidebarRowLayout> | null | undefined,
  categoryId: string,
  sectionId: string,
): SidebarRowLayout => {
  const next = layoutFields(layout);
  const added = storedIds(next.addedSectionIdsByCategory[categoryId]);
  delete next.labelBySection[sectionId];
  delete next.orderBySection[sectionId];
  if (added.includes(sectionId)) {
    next.addedSectionIdsByCategory[categoryId] = added.filter((id) => id !== sectionId);
  } else if (!next.deletedSectionIds.includes(sectionId)) {
    next.deletedSectionIds = [...next.deletedSectionIds, sectionId];
  }
  return next;
};

/**
 * The layout once the reader has moved a whole section to another category's sidebar: it arrives
 * as a section of the reader's (`sectionId`, new) under the same title, with its rows in order,
 * each of which then belongs to that category. The section it left goes: one the reader added is
 * forgotten, and one of the sidebar's own stays out of view while it has no rows.
 */
export const moveSidebarSectionToCategory = (
  layout: Partial<SidebarRowLayout> | null | undefined,
  move: {
    fromCategoryId: string;
    fromSectionId: string;
    label: string;
    rowIds: readonly string[];
    toCategoryId: string;
    sectionId: string;
  },
  homeCategoryOf: (rowId: string) => string | undefined,
): SidebarRowLayout => {
  const next = addSidebarSection(
    deleteSidebarSection(layout, move.fromCategoryId, move.fromSectionId),
    move.toCategoryId,
    move.sectionId,
  );
  next.labelBySection[move.sectionId] = move.label;
  next.orderBySection[move.sectionId] = [...move.rowIds];
  for (const rowId of move.rowIds) {
    if (homeCategoryOf(rowId) === move.toCategoryId) delete next.categoryByRow[rowId];
    else next.categoryByRow[rowId] = move.toCategoryId;
    next.sectionByRow[rowId] = move.sectionId;
  }
  return next;
};

/** The first page a category's sidebar lists, as the reader arranged it. */
export const getArrangedCategoryLandingItem = <T extends string>(
  category: SideNavV2Section<T>,
  categories: readonly SideNavV2Section<T>[],
  layout: Partial<SidebarRowLayout> | null | undefined,
  sectionOrder: readonly string[],
): SideNavV2Item<T> | undefined =>
  applySidebarRowLayout(
    applyStoredOrder(
      getCategorySidebarSectionsWithMoves(category, categories, layout),
      sectionOrder,
    ),
    layout,
  )
    .flatMap((section) => section.items)
    .find((item) => item.href != null && !item.disabled);

interface SidebarSectionOrderStore extends Record<string, unknown> {
  /** The `SIDEBAR_LAYOUT_VERSION` the orders were arranged against. */
  version: number;
  byCategory: Record<string, string[]>;
}

export const EMPTY_SIDEBAR_SECTION_ORDER: SidebarSectionOrderStore = {
  version: SIDEBAR_LAYOUT_VERSION,
  byCategory: {},
};

/** Stored section orders as the current default sidebars read them; see `migrateSidebarRowLayout`. */
export const migrateSidebarSectionOrder = (
  store: Partial<SidebarSectionOrderStore> | null | undefined,
): SidebarSectionOrderStore => {
  const stale = storedLayoutVersion(store) < 2;
  return {
    version: SIDEBAR_LAYOUT_VERSION,
    byCategory: Object.fromEntries(
      Object.entries(store?.byCategory ?? {}).flatMap(([categoryId, order]) =>
        Array.isArray(order)
          ? [[categoryId, stale ? migrateV1SectionOrder(categoryId, order) : order]]
          : [],
      ),
    ),
  };
};

/**
 * A layout store read and written as the current default sidebars read it: each read is migrated
 * from the version the store was arranged against, and the first write stores the migration. A
 * reader whose nav shows the default arrangement (`useShowsArrangedCategoryNav`) reads the
 * defaults, and one who may not arrange it (`useCanArrangeCategoryNav`) writes nothing.
 */
const useMigratedLayoutStore = <T extends Record<string, unknown>>(
  key: LocalStorageKey,
  empty: T,
  migrate: (stored: Partial<T> | null | undefined) => T,
) => {
  const showsArranged = useShowsArrangedCategoryNav();
  const canArrange = useCanArrangeCategoryNav();
  const [stored, setStored] = useLocalStorageSyncedState(key, empty);
  const value = useMemo(
    () => migrate(showsArranged ? stored : null),
    [migrate, showsArranged, stored],
  );
  const setValue = useCallback(
    (update: T | null | ((current: T) => T | null)) => {
      if (!canArrange) return;
      setStored((current) => (typeof update === "function" ? update(migrate(current)) : update));
    },
    [canArrange, migrate, setStored],
  );
  return [value, setValue] as const;
};

/** The reader's stored row layout; see `useMigratedLayoutStore`. */
export const useStoredSidebarRowLayout = () =>
  useMigratedLayoutStore(
    LocalStorageKey.PROJECT_NAV_SIDEBAR_ROW_LAYOUT,
    EMPTY_SIDEBAR_ROW_LAYOUT,
    migrateSidebarRowLayout,
  );

/** A category's stored section order. */
export const storedSectionOrder = (
  store: Partial<SidebarSectionOrderStore> | null | undefined,
  categoryId: string,
) => {
  const order = store?.byCategory?.[categoryId];
  return Array.isArray(order) ? order : [];
};

/** The reader's section orders for every category sidebar, kept across reloads. */
export const useSidebarSectionOrders = () => {
  const [sectionOrders, setSectionOrders] = useMigratedLayoutStore(
    LocalStorageKey.PROJECT_NAV_SIDEBAR_SECTION_ORDER,
    EMPTY_SIDEBAR_SECTION_ORDER,
    migrateSidebarSectionOrder,
  );
  return { sectionOrders, setSectionOrders };
};

/** The reader's order for one category sidebar's sections, kept across reloads. */
export const useSidebarSectionOrder = (categoryId: string) => {
  const { sectionOrders, setSectionOrders } = useSidebarSectionOrders();
  const stored = sectionOrders.byCategory?.[categoryId];
  const sectionOrder = useMemo(() => (Array.isArray(stored) ? stored : []), [stored]);
  /** Stores the order of the sections the reader can see; see `mergeVisibleOrder`. */
  const setVisibleSectionOrder = useCallback(
    (visibleOrder: readonly string[]) =>
      setSectionOrders((current) => ({
        version: SIDEBAR_LAYOUT_VERSION,
        byCategory: {
          ...current?.byCategory,
          [categoryId]: mergeVisibleOrder(storedSectionOrder(current, categoryId), visibleOrder),
        },
      })),
    [categoryId, setSectionOrders],
  );

  return { sectionOrder, setVisibleSectionOrder };
};

/** The reader's arrangement of every category sidebar's rows, kept across reloads. */
export const useSidebarRowLayout = () => {
  const [rowLayout, setRowLayout] = useStoredSidebarRowLayout();
  /** Stores rows the reader rearranged; see `recordSidebarRowArrangement`. */
  const arrangeRows = useCallback(
    (
      arrangement: readonly { sectionId: string; itemIds: readonly string[] }[],
      homeSectionOf: (rowId: string) => string | undefined,
    ) =>
      setRowLayout((current) => recordSidebarRowArrangement(current, arrangement, homeSectionOf)),
    [setRowLayout],
  );
  /** Stores a row moved to another category; see `moveSidebarRowToCategory`. */
  const moveRowToCategory = useCallback(
    (rowId: string, categoryId: string, homeCategoryId: string) =>
      setRowLayout((current) =>
        moveSidebarRowToCategory(current, rowId, categoryId, homeCategoryId),
      ),
    [setRowLayout],
  );

  const renameSection = useCallback(
    (sectionId: string, label: string, defaultLabel: string | undefined) =>
      setRowLayout((current) => renameSidebarSection(current, sectionId, label, defaultLabel)),
    [setRowLayout],
  );
  const addSection = useCallback(
    (categoryId: string, sectionId: string) =>
      setRowLayout((current) => addSidebarSection(current, categoryId, sectionId)),
    [setRowLayout],
  );
  const deleteSection = useCallback(
    (categoryId: string, sectionId: string) =>
      setRowLayout((current) => deleteSidebarSection(current, categoryId, sectionId)),
    [setRowLayout],
  );

  /** Stores a section moved to another category; see `moveSidebarSectionToCategory`. */
  const moveSectionToCategory = useCallback(
    (
      move: Parameters<typeof moveSidebarSectionToCategory>[1],
      homeCategoryOf: (rowId: string) => string | undefined,
    ) => setRowLayout((current) => moveSidebarSectionToCategory(current, move, homeCategoryOf)),
    [setRowLayout],
  );
  const renameCategory = useCallback(
    (categoryId: string, label: string, defaultLabel: string | undefined) =>
      setRowLayout((current) => renameSidebarCategory(current, categoryId, label, defaultLabel)),
    [setRowLayout],
  );

  return {
    rowLayout,
    arrangeRows,
    moveRowToCategory,
    renameSection,
    addSection,
    deleteSection,
    renameCategory,
    moveSectionToCategory,
  };
};

const hasValues = (record: unknown) =>
  record != null && typeof record === "object" && Object.keys(record).length > 0;

/**
 * Whether the reader has rearranged the category nav, and a command that puts it all back: the
 * rail's category icon order, and in every sidebar its rows, row moves, sections and their titles,
 * category names, and section orders. Pinned shortcuts are the reader's own and stay.
 */
export const useSidebarLayoutReset = () => {
  const [rowLayout, setRowLayout] = useStoredSidebarRowLayout();
  const { sectionOrders, setSectionOrders } = useSidebarSectionOrders();
  const [categoryOrder, setCategoryOrder] = useLocalStorageSyncedState(
    LocalStorageKey.PROJECT_NAV_RAIL_CATEGORY_ORDER,
    EMPTY_RAIL_CATEGORY_ORDER,
  );
  const rearranged =
    hasValues(rowLayout.orderBySection) ||
    hasValues(rowLayout.sectionByRow) ||
    hasValues(rowLayout.categoryByRow) ||
    hasValues(rowLayout.labelBySection) ||
    hasValues(rowLayout.deletedSectionIds) ||
    hasValues(rowLayout.addedSectionIdsByCategory) ||
    hasValues(rowLayout.labelByCategory) ||
    hasValues(sectionOrders.byCategory) ||
    hasValues(categoryOrder.itemIds);
  const reset = useCallback(() => {
    setRowLayout(null);
    setSectionOrders(null);
    setCategoryOrder(null);
  }, [setCategoryOrder, setRowLayout, setSectionOrders]);

  return { rearranged, reset };
};
